import { useQuery } from '@tanstack/react-query'
import { PieChart, PieSlice, PieCenter } from '@/components/charts'
import { useAppStore } from '@/store'
import { fetchTaxRevenues } from '@/data/eurostat'
import { useNavigate } from 'react-router'

// Distinct colors — one per category
const COLORS: Record<string, string> = {
  D211: '#e94560',   // TVA — rouge
  D212: '#ff6b35',   // Droits d'importation — orange
  D214: '#f7c948',   // Autres impôts produits — jaune
  D29:  '#00b894',   // Autres impôts production — vert
  D51A: '#0984e3',   // IR — bleu
  D51B: '#6c5ce7',   // IS — violet
  D59:  '#e17055',   // Autres impôts courants — terracotta
  D611: '#00cec9',   // Cotis. employeurs — turquoise
  D612: '#fd79a8',   // Cotis. salariés — rose
  D613: '#a29bfe',   // Cotis. non-salariés — lavande
}

export default function RecettesByType() {
  const { selectedYear } = useAppStore()
  const navigate = useNavigate()

  const taxesQuery = useQuery({
    queryKey: ['taxes', selectedYear],
    queryFn: () => fetchTaxRevenues(selectedYear),
    staleTime: 1000 * 60 * 60,
  })

  const taxes = taxesQuery.data ?? []
  const total = taxes.reduce((s, t) => s + t.value, 0)

  // Flatten all tax items into pie data (from the grouped treemap structure)
  const pieData = taxes.map((t) => ({
    label: t.shortLabel,
    value: Math.round(t.value),
    color: COLORS[t.code] ?? '#ccc',
    code: t.code,
  }))

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold">Recettes fiscales — {selectedYear}</h2>
        <p className="text-text-muted mt-1">
          Prélèvements obligatoires des administrations publiques (S13)
        </p>
      </div>

      {taxesQuery.isLoading && (
        <div className="bg-card rounded-lg shadow p-8 text-center text-text-muted">
          Chargement des données Eurostat…
        </div>
      )}

      {taxes.length > 0 && (
        <>
          {/* Total */}
          <div className="bg-card rounded-lg shadow p-6 mb-6">
            <div className="flex items-baseline gap-4">
              <span className="text-text-muted">Total :</span>
              <span className="text-4xl font-bold text-nav">
                {(total / 1000).toFixed(0)} Md€
              </span>
              <span className="text-text-muted text-sm">({taxes.length} catégories)</span>
            </div>
          </div>

          {/* Pie Chart */}
          <div className="bg-card rounded-lg shadow p-6 mb-6">
            <h3 className="font-semibold text-lg mb-4">Répartition des recettes fiscales</h3>
            <div className="flex justify-center">
              <PieChart data={pieData} size={420} innerRadius={90}>
                {pieData.map((item, i) => (
                  <PieSlice
                    key={item.code}
                    index={i}
                    color={item.color}
                  />
                ))}
                <PieCenter defaultLabel="Total" />
              </PieChart>
            </div>
          </div>

          {/* Detail table */}
          <div className="bg-card rounded-lg shadow p-6">
            <h3 className="font-semibold text-lg mb-4">Détail par type d'impôt</h3>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-text-muted">
                  <th className="pb-2">Type</th>
                  <th className="pb-2 text-right">Montant</th>
                  <th className="pb-2 text-right">% du total</th>
                  <th className="pb-2 text-right w-32">Répartition</th>
                </tr>
              </thead>
              <tbody>
                {taxes
                  .sort((a, b) => b.value - a.value)
                  .map((tax) => (
                    <tr
                      key={tax.code}
                      className="border-b border-border hover:bg-muted cursor-pointer"
                      onClick={() => navigate(`/detail/tax/${tax.code}`)}
                    >
                      <td className="py-3 flex items-center gap-2">
                        <span
                          className="w-3 h-3 rounded-full inline-block"
                          style={{ backgroundColor: COLORS[tax.code] ?? '#ccc' }}
                        />
                        {tax.shortLabel}
                      </td>
                      <td className="py-3 text-right font-semibold">
                        {(tax.value / 1000).toFixed(0)} Md€
                      </td>
                      <td className="py-3 text-right">{tax.pctOfTotal.toFixed(1)}%</td>
                      <td className="py-3 text-right">
                        <div className="inline-block w-24 bg-muted rounded-full h-2">
                          <div
                            className="h-full rounded-full"
                            style={{
                              width: `${tax.pctOfTotal}%`,
                              backgroundColor: COLORS[tax.code] ?? '#ccc',
                            }}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <p className="text-xs text-text-muted mt-4">
        Source : Eurostat gov_10a_taxag (M€, monnaie nationale, S13)
      </p>
    </div>
  )
}
